if (!customElements.get('case-studies-component')) {
  class CaseStudiesComponent extends HTMLElement {
    connectedCallback() {
      this.tabsContainer = this.querySelector('[data-case-studies-tabs]');
      this.tabs = Array.from(this.querySelectorAll('[data-case-study-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-case-study-panel]'));
      this.media = Array.from(this.querySelectorAll('[data-case-study-media]'));
      this.mediaPlayers = this.media.map((item) => item.querySelector('[data-case-study-video]'));

      if (!this.tabsContainer || this.tabs.length === 0) return;

      this.activeIndex = 0;
      this.visibilityRatios = new Map();
      this.isProgrammaticScroll = false;

      this.syncActiveMedia();

      this.tabClickHandlers = this.tabs.map((tab, index) => {
        const handler = () => {
          if (this.isDragging) return;
          this.selectIndex(index);
        };
        tab.addEventListener('click', handler);
        return handler;
      });

      this.tabKeydownHandlers = this.tabs.map((tab, index) => {
        const handler = (event) => this.onTabKeydown(event, index);
        tab.addEventListener('keydown', handler);
        return handler;
      });

      this.intersectionObserver = new IntersectionObserver(this.onIntersect.bind(this), {
        root: this.tabsContainer,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
      this.tabs.forEach((tab) => this.intersectionObserver.observe(tab));

      this.onBlockSelect = (event) => {
        const index = this.panels.indexOf(event.target);
        if (index !== -1) this.selectIndex(index);
      };
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      this.initDragScroll();
      this.initTabBracket();
    }

    disconnectedCallback() {
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      window.clearTimeout(this.scrollResetTimeout);

      if (this.tabs) {
        this.tabs.forEach((tab, i) => {
          if (this.tabClickHandlers) tab.removeEventListener('click', this.tabClickHandlers[i]);
          if (this.tabKeydownHandlers) tab.removeEventListener('keydown', this.tabKeydownHandlers[i]);
        });
      }

      if (this.onBlockSelect) this.removeEventListener('shopify:block:select', this.onBlockSelect);

      if (this.removeDragScrollListeners) this.removeDragScrollListeners();

      if (this.tabBracket) this.tabBracket.destroy();
    }

    initTabBracket() {
      this.tabBracket = new BracketIndicator({
        container: this.tabsContainer,
        items: this.tabs,
        className: 'case-studies__tab-bracket',
        insetX: 13,
        insetY: 4,
        getRestingItem: () => this.tabs[this.activeIndex],
      });
    }

    initDragScroll() {
      const container = this.tabsContainer;
      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;

      const onMouseDown = (event) => {
        isDown = true;
        this.isDragging = false;
        startX = event.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
        event.preventDefault();
      };

      const onMouseLeave = () => {
        isDown = false;
      };

      const onMouseUp = () => {
        isDown = false;
        window.setTimeout(() => {
          this.isDragging = false;
          this.syncActiveMedia();
        }, 50);
      };

      const onMouseMove = (event) => {
        if (!isDown) return;
        event.preventDefault();
        const walk = (event.pageX - container.offsetLeft - startX) * 1.5;
        if (Math.abs(walk) > 5) this.isDragging = true;
        container.scrollLeft = scrollLeft - walk;
      };

      container.addEventListener('mousedown', onMouseDown);
      container.addEventListener('mouseleave', onMouseLeave);
      container.addEventListener('mouseup', onMouseUp);
      container.addEventListener('mousemove', onMouseMove);

      this.removeDragScrollListeners = () => {
        container.removeEventListener('mousedown', onMouseDown);
        container.removeEventListener('mouseleave', onMouseLeave);
        container.removeEventListener('mouseup', onMouseUp);
        container.removeEventListener('mousemove', onMouseMove);
      };
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        this.visibilityRatios.set(entry.target, entry.intersectionRatio);
      });

      if (this.isProgrammaticScroll) return;

      let mostVisibleTab = null;
      let highestRatio = 0;
      this.tabs.forEach((tab) => {
        const ratio = this.visibilityRatios.get(tab) || 0;
        if (ratio > highestRatio) {
          highestRatio = ratio;
          mostVisibleTab = tab;
        }
      });

      if (mostVisibleTab) {
        this.setActive(this.tabs.indexOf(mostVisibleTab));
      }
    }

    onTabKeydown(event, index) {
      let newIndex = null;
      if (event.key === 'ArrowRight') {
        newIndex = Math.min(index + 1, this.tabs.length - 1);
      } else if (event.key === 'ArrowLeft') {
        newIndex = Math.max(index - 1, 0);
      }
      if (newIndex === null || newIndex === index) return;
      event.preventDefault();
      this.tabs[newIndex].focus({ preventScroll: true });
      this.selectIndex(newIndex);
    }

    selectIndex(index) {
      this.setActive(index);

      const tab = this.tabs[index];
      const container = this.tabsContainer;
      const tabRect = tab.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const targetScrollLeft =
        container.scrollLeft + (tabRect.left + tabRect.width / 2) - (containerRect.left + container.clientWidth / 2);

      this.isProgrammaticScroll = true;
      container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
      window.clearTimeout(this.scrollResetTimeout);
      this.scrollResetTimeout = window.setTimeout(() => {
        this.isProgrammaticScroll = false;
      }, 600);
    }

    setActive(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;

      this.tabs.forEach((tab, i) => {
        this.applyPositionClass(tab, i, index);
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.setAttribute('tabindex', i === index ? '0' : '-1');
      });
      this.panels.forEach((panel, i) => this.applyPositionClass(panel, i, index));
      this.media.forEach((item, i) => this.applyPositionClass(item, i, index));

      if (!this.isDragging) this.syncActiveMedia();

      this.tabBracket.show(this.tabs[index]);
    }

    syncActiveMedia() {
      this.mediaPlayers.forEach((player, i) => {
        if (!player) return;
        if (i === this.activeIndex) {
          this.playMedia(player);
        } else {
          this.pauseMedia(player);
        }
      });
    }

    playMedia(player) {
      if (player.tagName === 'VIDEO') {
        const startedPlaying = player.play();
        if (startedPlaying && startedPlaying.catch) startedPlaying.catch(() => {});
      } else if (player.tagName === 'IFRAME' && player.dataset.src) {
        player.src = player.dataset.src;
      }
    }

    pauseMedia(player) {
      if (player.tagName === 'VIDEO') {
        player.pause();
      } else if (player.tagName === 'IFRAME' && player.dataset.src) {
        player.removeAttribute('src');
      }
    }

    applyPositionClass(element, index, activeIndex) {
      element.classList.remove('is-active', 'is-prev', 'is-next');
      if (index === activeIndex) {
        element.classList.add('is-active');
      } else {
        element.classList.add(index < activeIndex ? 'is-prev' : 'is-next');
      }
    }
  }

  customElements.define('case-studies-component', CaseStudiesComponent);
}
